import React, { useState, useEffect } from 'react';
import BackToDashboardButton from './BackToDashboardButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlus, faTrash, faEdit, faCheck, faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import './CustomizationMapping.css';

const CustomizationMapping = ({ goTo }) => {
  const [loading, setLoading] = useState(true);
  const [customizationOptions, setCustomizationOptions] = useState([]);
  const [customizationGroups, setCustomizationGroups] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [activeTab, setActiveTab] = useState('options'); // 'options', 'groups', 'menu-items'
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'option', 'group', 'link-group'
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  
  const API_URL = process.env.REACT_APP_API_URL;
  const businessId = localStorage.getItem('businessId');

  // Business-scoped endpoints require a verified token; the business is taken from
  // it server-side, so the id header is only a hint that must agree with the token.
  const authHeaders = (extra = {}) => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'x-business-id': businessId,
    ...extra
  });
  const jsonAuthHeaders = () => authHeaders({ 'Content-Type': 'application/json' });

  // Customization types
  const CUSTOMIZATION_TYPES = [
    { value: 'addon', label: '➕ Add-on' },
    { value: 'removal', label: '➖ Removal' },
    { value: 'spice_level', label: '🌶️ Spice Level' },
    { value: 'cooking_preference', label: '👨‍🍳 Cooking Preference' },
    { value: 'size_adjustment', label: '📏 Size Adjustment' }
  ];

  const SELECTION_TYPES = [
    { value: 'single', label: 'Single Selection' },
    { value: 'multiple', label: 'Multiple Selection' },
    { value: 'required_single', label: 'Required Single Selection' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCustomizationOptions(),
        loadCustomizationGroups(),
        loadMenuItems()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomizationOptions = async () => {
    try {
      const response = await fetch(`${API_URL}/customizations/options`, {
        headers: authHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setCustomizationOptions(data.options);
      }
    } catch (error) {
      console.error('Error loading customization options:', error);
    }
  };

  const loadCustomizationGroups = async () => {
    try {
      const response = await fetch(`${API_URL}/customizations/groups?include_options=true`, {
        headers: authHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setCustomizationGroups(data.groups);
      }
    } catch (error) {
      console.error('Error loading customization groups:', error);
    }
  };

  const loadMenuItems = async () => {
    try {
      const response = await fetch(`${API_URL}/recipes`, {
        headers: authHeaders()
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        setMenuItems(data);
      }
    } catch (error) {
      console.error('Error loading menu items:', error);
    }
  };

  const loadMenuItemCustomizations = async (menuItemId) => {
    try {
      const response = await fetch(`${API_URL}/api/customizations/menu-items/${menuItemId}`, {
        headers: authHeaders()
      });
      const data = await response.json();
      if (data.success) {
        return data.groups;
      }
    } catch (error) {
      console.error('Error loading menu item customizations:', error);
    }
    return [];
  };

  const handleSaveOption = async () => {
    try {
      const url = editingItem 
        ? `${API_URL}/api/customizations/options/${editingItem.id}`
        : `${API_URL}/api/customizations/options`;
      
      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: jsonAuthHeaders(),
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        await loadCustomizationOptions();
        setShowModal(false);
        setFormData({});
        setEditingItem(null);
      } else {
        alert(data.error || 'Failed To Save Option');
      }
    } catch (error) {
      console.error('Error saving option:', error);
      alert('Failed To Save Option');
    }
  };

  const handleSaveGroup = async () => {
    try {
      const url = editingItem 
        ? `${API_URL}/api/customizations/groups/${editingItem.id}`
        : `${API_URL}/api/customizations/groups`;
      
      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: jsonAuthHeaders(),
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        await loadCustomizationGroups();
        setShowModal(false);
        setFormData({});
        setEditingItem(null);
      } else {
        alert(data.error || 'Failed To Save Group');
      }
    } catch (error) {
      console.error('Error saving group:', error);
      alert('Failed To Save Group');
    }
  };

  const handleDeleteOption = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customization option?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/customizations/options/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      const data = await response.json();
      if (data.success) {
        await loadCustomizationOptions();
      } else {
        alert(data.error || 'Failed To Delete Option');
      }
    } catch (error) {
      console.error('Error deleting option:', error);
      alert('Failed To Delete Option');
    }
  };

  const handleLinkGroupToMenuItem = async (menuItemId, groupId) => {
    try {
      const response = await fetch(`${API_URL}/api/customizations/menu-items/${menuItemId}/groups`, {
        method: 'POST',
        headers: jsonAuthHeaders(),
        body: JSON.stringify({
          group_id: groupId,
          is_required: false,
          display_order: 0
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Group linked to menu item successfully!');
        if (selectedMenuItem) {
          const groups = await loadMenuItemCustomizations(selectedMenuItem.id);
          setSelectedMenuItem({ ...selectedMenuItem, customization_groups: groups });
        }
      } else {
        alert(data.error || 'Failed To Link Group');
      }
    } catch (error) {
      console.error('Error linking group:', error);
      alert('Failed To Link Group');
    }
  };

  const handleUnlinkGroupFromMenuItem = async (menuItemId, groupId) => {
    if (!window.confirm('Remove this customization group from the menu item?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/customizations/menu-items/${menuItemId}/groups/${groupId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      const data = await response.json();
      if (data.success) {
        if (selectedMenuItem) {
          const groups = await loadMenuItemCustomizations(selectedMenuItem.id);
          setSelectedMenuItem({ ...selectedMenuItem, customization_groups: groups });
        }
      } else {
        alert(data.error || 'Failed To Unlink Group');
      }
    } catch (error) {
      console.error('Error unlinking group:', error);
      alert('Failed To Unlink Group');
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    if (item) {
      setFormData({ ...item });
    } else {
      setFormData({});
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType(null);
    setEditingItem(null);
    setFormData({});
  };

  const selectMenuItem = async (item) => {
    const groups = await loadMenuItemCustomizations(item.id);
    setSelectedMenuItem({ ...item, customization_groups: groups });
  };

  if (loading) {
    return (
      <div className="customization-mapping-container">
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div>Loading customization settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="customization-mapping-container">
      <BackToDashboardButton previousScreen="dashboard" goTo={goTo} />
      {/* Header */}
      <div className="customization-header">
        <h2 className="page-title">Customization Mapping</h2>
      </div>

      {/* Tabs */}
      <div className="customization-tabs">
        <button
          className={`tab ${activeTab === 'options' ? 'active' : ''}`}
          onClick={() => setActiveTab('options')}
        >
          Customization Options
        </button>
        <button
          className={`tab ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          Customization Groups
        </button>
        <button
          className={`tab ${activeTab === 'menu-items' ? 'active' : ''}`}
          onClick={() => setActiveTab('menu-items')}
        >
          Menu Item Setup
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Customization Options Tab */}
        {activeTab === 'options' && (
          <div className="options-tab">
            <div className="section-header">
              <h3>Customization Options</h3>
              <button
                className="btn-primary"
                onClick={() => openModal('option')}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Option
              </button>
            </div>

            <div className="options-grid">
              {CUSTOMIZATION_TYPES.map(type => {
                const typeOptions = customizationOptions.filter(opt => opt.type === type.value);
                return (
                  <div key={type.value} className="option-type-card">
                    <h4>{type.label}</h4>
                    <div className="option-list">
                      {typeOptions.length === 0 ? (
                        <div className="empty-state">No Options Yet</div>
                      ) : (
                        typeOptions.map(option => (
                          <div key={option.id} className="option-item">
                            <div className="option-details">
                              <span className="option-name">{option.name}</span>
                              {option.price_adjustment > 0 && (
                                <span className="option-price">+₹{option.price_adjustment}</span>
                              )}
                            </div>
                            <div className="option-actions">
                              <button
                                className="btn-icon"
                                onClick={() => openModal('option', option)}
                                title="Edit"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                className="btn-icon btn-danger"
                                onClick={() => handleDeleteOption(option.id)}
                                title="Delete"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Customization Groups Tab */}
        {activeTab === 'groups' && (
          <div className="groups-tab">
            <div className="section-header">
              <h3>Customization Groups</h3>
              <button
                className="btn-primary"
                onClick={() => openModal('group')}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Group
              </button>
            </div>

            <div className="groups-list">
              {customizationGroups.length === 0 ? (
                <div className="empty-state">No Groups Created Yet</div>
              ) : (
                customizationGroups.map(group => (
                  <div key={group.id} className="group-card">
                    <div className="group-header">
                      <h4>{group.name}</h4>
                      <span className="group-type">{group.selection_type}</span>
                    </div>
                    {group.description && (
                      <p className="group-description">{group.description}</p>
                    )}
                    <div className="group-options">
                      {group.options && group.options.length > 0 ? (
                        <div className="option-chips">
                          {group.options.map(opt => (
                            <span key={opt.id} className="option-chip">
                              {opt.name}
                              {opt.price_adjustment > 0 && ` (+₹${opt.price_adjustment})`}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="empty-state-small">No Options In This Group</div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Menu Item Setup Tab */}
        {activeTab === 'menu-items' && (
          <div className="menu-items-tab">
            <div className="menu-items-layout">
              {/* Left: Menu Items List */}
              <div className="menu-items-list">
                <h3>Menu Items</h3>
                {menuItems.map(item => (
                  <div
                    key={item.id}
                    className={`menu-item-card ${selectedMenuItem?.id === item.id ? 'selected' : ''}`}
                    onClick={() => selectMenuItem(item)}
                  >
                    <div className="menu-item-name">{item.name}</div>
                    <div className="menu-item-price">₹{item.price}</div>
                  </div>
                ))}
              </div>

              {/* Right: Customizations for Selected Item */}
              <div className="menu-item-customizations">
                {selectedMenuItem ? (
                  <>
                    <div className="section-header">
                      <h3>Customizations for {selectedMenuItem.name}</h3>
                      <button
                        className="btn-primary"
                        onClick={() => {
                          const availableGroups = customizationGroups.filter(
                            g => !selectedMenuItem.customization_groups?.some(cg => cg.id === g.id)
                          );
                          if (availableGroups.length === 0) {
                            alert('All Groups Are Already Linked To This Item');
                            return;
                          }
                          const groupId = prompt(`Enter group ID (${availableGroups.map(g => `${g.id}: ${g.name}`).join(', ')})`);
                          if (groupId) {
                            handleLinkGroupToMenuItem(selectedMenuItem.id, parseInt(groupId));
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add Group
                      </button>
                    </div>

                    {selectedMenuItem.customization_groups && selectedMenuItem.customization_groups.length > 0 ? (
                      <div className="linked-groups">
                        {selectedMenuItem.customization_groups.map(group => (
                          <div key={group.id} className="linked-group-card">
                            <div className="group-header">
                              <h4>{group.name}</h4>
                              <button
                                className="btn-icon btn-danger"
                                onClick={() => handleUnlinkGroupFromMenuItem(selectedMenuItem.id, group.id)}
                                title="Remove Group"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                            <div className="group-options">
                              {group.options.map(opt => (
                                <span key={opt.id} className="option-chip">
                                  {opt.name}
                                  {opt.price_adjustment > 0 && ` (+₹${opt.price_adjustment})`}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state">
                        No customization groups linked yet. Click "Add Group" to start.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-state">
                    Select a menu item to manage its customizations
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalType === 'option' && (editingItem ? 'Edit Option' : 'Add Option')}
                {modalType === 'group' && (editingItem ? 'Edit Group' : 'Add Group')}
              </h3>
              <button className="btn-icon" onClick={closeModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              {modalType === 'option' && (
                <>
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Extra Cheese"
                    />
                  </div>

                  <div className="form-group">
                    <label>Type *</label>
                    <select
                      value={formData.type || ''}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      <option value="">Select Type</option>
                      {CUSTOMIZATION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional Description"
                      rows={2}
                    />
                  </div>

                  <div className="form-group">
                    <label>Price Adjustment (₹)</label>
                    <input
                      type="number"
                      value={formData.price_adjustment || 0}
                      onChange={(e) => setFormData({ ...formData, price_adjustment: parseFloat(e.target.value) })}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      value={formData.display_order || 0}
                      onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                      placeholder="0"
                    />
                  </div>
                </>
              )}

              {modalType === 'group' && (
                <>
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Spice Level"
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional Description"
                      rows={2}
                    />
                  </div>

                  <div className="form-group">
                    <label>Selection Type *</label>
                    <select
                      value={formData.selection_type || 'single'}
                      onChange={(e) => setFormData({ ...formData, selection_type: e.target.value })}
                    >
                      {SELECTION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Min Selections</label>
                      <input
                        type="number"
                        value={formData.min_selections || 0}
                        onChange={(e) => setFormData({ ...formData, min_selections: parseInt(e.target.value) })}
                        min="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>Max Selections</label>
                      <input
                        type="number"
                        value={formData.max_selections || 1}
                        onChange={(e) => setFormData({ ...formData, max_selections: parseInt(e.target.value) })}
                        min="1"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={modalType === 'option' ? handleSaveOption : handleSaveGroup}
              >
                <FontAwesomeIcon icon={faSave} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizationMapping;
